'use client';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { GlassCarouselArrow } from '@web/molecules';
import { useCarousel, CarouselPauseProvider } from '../../hooks';
import { HeroSlide } from '../HeroSlide';
import { HeroStats } from '../HeroStats';
import {
  StyledFullBleedSliderHero,
  SliderViewport,
  SlideWrapper,
  HeroStatsSection,
  DotIndicators,
  DotButton,
} from './styles.js';

const getSlideStats = slide => {
  const accessory = slide.accessoryComponent;
  return accessory?.component === 'hero_stats' && accessory.stats?.length
    ? accessory
    : null;
};

const getHeroStatsForSection = (slides, activeSlide) =>
  getSlideStats(activeSlide) ??
  slides.map(getSlideStats).find(stats => stats != null) ??
  null;

export const FullBleedSliderHero = ({ slides = [], carouselConfig = null }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const {
    activeIndex,
    outgoingIndex,
    direction,
    goTo,
    goNext,
    goPrev,
    animationConfig,
    arrowsVisible,
    dotsVisible,
    handlers,
  } = useCarousel({
    count: slides.length,
    autoplayEnabled: carouselConfig?.autoplayEnabled ?? true,
    autoplayIntervalMs: carouselConfig?.autoplayIntervalMs ?? 6000,
    transitionDurationMs: carouselConfig?.transitionDurationMs ?? 640,
    transitionEasing:
      carouselConfig?.transitionEasing ?? 'cubic-bezier(0.76, 0, 0.24, 1)',
    resumeAfterMs: carouselConfig?.resumeAfterMs ?? 8000,
    pauseOnHover: carouselConfig?.pauseOnHover ?? true,
    paused: modalOpen,
    showArrowControls: carouselConfig?.showArrowControls ?? true,
    showDotIndicators: carouselConfig?.showDotIndicators ?? true,
  });

  if (!slides.length) {
    return null;
  }

  const activeSlide = slides[activeIndex];
  const sectionStats = getHeroStatsForSection(slides, activeSlide);

  const renderSlide = (slide, key, wrapperProps = {}) => (
    <SlideWrapper key={key} {...wrapperProps}>
      <HeroSlide {...slide} />
    </SlideWrapper>
  );

  return (
    <CarouselPauseProvider onModalOpenChange={setModalOpen}>
      <StyledFullBleedSliderHero>
        {arrowsVisible && (
          <GlassCarouselArrow
            direction="prev"
            onClick={goPrev}
            aria-label="Previous Slide"
            iconColor="text.lightest"
            edgePosition="inset"
          />
        )}
        <SliderViewport {...handlers}>
          {outgoingIndex !== null ? (
            <>
              {renderSlide(activeSlide, `in-${activeIndex}`, {
                $entering: true,
                $direction: direction,
                $animation: animationConfig,
              })}
              {renderSlide(slides[outgoingIndex], `out-${outgoingIndex}`, {
                $exiting: true,
                $direction: direction,
                $animation: animationConfig,
              })}
            </>
          ) : (
            renderSlide(activeSlide, `static-${activeIndex}`)
          )}
          {dotsVisible && (
            <DotIndicators>
              {slides.map((_, index) => (
                <DotButton
                  key={index}
                  type="button"
                  $active={index === activeIndex}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={index === activeIndex ? 'true' : undefined}
                  onClick={() => goTo(index)}
                />
              ))}
            </DotIndicators>
          )}
        </SliderViewport>
        {sectionStats && (
          <HeroStatsSection>
            <HeroStats {...sectionStats} />
          </HeroStatsSection>
        )}
        {arrowsVisible && (
          <GlassCarouselArrow
            direction="next"
            onClick={goNext}
            aria-label="Next Slide"
            iconColor="text.lightest"
            edgePosition="inset"
          />
        )}
      </StyledFullBleedSliderHero>
    </CarouselPauseProvider>
  );
};

FullBleedSliderHero.displayName = 'FullBleedSliderHero';

FullBleedSliderHero.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.object),
  carouselConfig: PropTypes.shape({
    autoplayEnabled: PropTypes.bool,
    autoplayIntervalMs: PropTypes.number,
    transitionDurationMs: PropTypes.number,
    transitionEasing: PropTypes.string,
    resumeAfterMs: PropTypes.number,
    pauseOnHover: PropTypes.bool,
    showArrowControls: PropTypes.bool,
    showDotIndicators: PropTypes.bool,
  }),
};
